import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DataSource, SiteRagReport } from '../../types/siteRag';

/** The absence judge needs the frozen coverage limitation behind a FEMA
 * citation, not just its bibliography title. This only quotes an incomplete
 * polygon query; it never treats zero returned hazards as verified absence. */
export function frozenFloodRefEvidence(
  ref: DataSource | undefined,
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
): string | undefined {
  if (!sed || report.sedId !== sed.id || report.sedVersionUsed !== sed.version
    || !Number.isInteger(sed.version) || sed.version <= 0) return;
  const section = sed.flood;
  const coverage = section?.data?.screeningCoverage;
  if (!ref || ref.field !== 'flood' || ref.documentId || ref.needsVerification
    || !section?.data || section.status !== 'verified' || section.attemptResult !== 'success'
    || section.attemptedProvider !== 'fema-flood'
    || section.data.analysisMethod !== 'polygon-intersection'
    || section.sourceUrl !== 'https://msc.fema.gov/portal/home'
    || !/^FEMA National Flood Hazard Layer(?: \(cached\))?$/.test(section.source)
    || ref.source !== section.source || ref.url !== section.sourceUrl
    || ref.retrievedAt !== section.retrievedAt || !Number.isFinite(Date.parse(section.retrievedAt))
    || ref.qualification !== 'screening' || section.qualification !== 'screening'
    || ref.sourceGrade !== 'authoritative-government' || section.sourceGrade !== 'authoritative-government'
    || !coverage || !['partial', 'unknown'].includes(coverage.status)
    || typeof coverage.reason !== 'string' || !coverage.reason.trim() || coverage.reason.length > 2000) return;
  // Same SED identity alone cannot lend another selected polygon's coverage.
  // An unavailable FEMA context binding remains unavailable; these hashes
  // bind only the SED/report study area, not a new provider completeness proof.
  const scope = sed.studyAreaScope;
  const reportScope = report.studyAreaScope;
  if (!scope || !reportScope || scope.schemaVersion !== 1 || reportScope.schemaVersion !== 1
    || !/^[a-f0-9]{64}$/.test(scope.geometryHash) || !/^[a-f0-9]{64}$/.test(scope.selectionHash)
    || scope.geometryHash !== reportScope.geometryHash || scope.selectionHash !== reportScope.selectionHash
    || scope.revision !== reportScope.revision) return;
  const point = section.queryPoint;
  if (!point || point.source !== 'boundary-sample'
    || !Number.isFinite(point.lat) || Math.abs(point.lat) > 90
    || !Number.isFinite(point.lng) || Math.abs(point.lng) > 180
    || point.lat !== sed.location?.lat || point.lng !== sed.location?.lng
    || point.lat !== report.location?.lat || point.lng !== report.location?.lng) return;
  if (['screeningCoverage', 'floodZone', 'sfha', 'areaAnalysis', 'analysisMethod']
    .some(field => section.docFieldSources?.[field] != null)) return;
  const fractions = [coverage.mappedFraction, coverage.unavailableFraction, coverage.conflictFraction];
  if (fractions.some(value => value != null && (!Number.isFinite(value) || value < 0 || value > 1))) return;
  return `\nFrozen FEMA screening coverage (quoted evidence, not a verdict): ${JSON.stringify({
    sedId: sed.id, sedVersion: sed.version,
    source: section.source, sourceUrl: section.sourceUrl, retrievedAt: section.retrievedAt,
    queryPoint: point, analysisMethod: section.data.analysisMethod,
    studyArea: { geometryHash: scope.geometryHash, selectionHash: scope.selectionHash, revision: scope.revision },
    screeningCoverage: { status: coverage.status, reason: coverage.reason,
      mappedFraction: coverage.mappedFraction, unavailableFraction: coverage.unavailableFraction,
      conflictFraction: coverage.conflictFraction },
    scopeLimit: 'This records incomplete evidence for the frozen query only. Unknown or unavailable coverage does not establish flood absence, flood-free land, complete parcel mapping, permit clearance or land-use authority. Legacy area totals and observed Zone X cannot resolve the withheld coverage.',
  })}`;
}
